import React, { useMemo } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import styles from "./CalendaCalendar.module.css";
import { dayKeyBRT, getProfessionalColor } from "./calendarHelpers";

// Quantos dots mostrar antes de colapsar em "+X".
const MAX_DOTS = 4;
// Início de semana segunda-feira (consistente com a view Semana, weekStartsOn:1).
const WEEK_STARTS_ON = 1;

const WEEKDAY_LABELS = ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"];

/**
 * View mensal: semanas × 7 dias. Cada célula traz o número do dia + dot
 * indicators (um ponto colorido por agendamento, cor por profissional). Não
 * renderiza cards/eventos completos — só dots, conforme briefing. Clicar num
 * dia troca para a view Dia naquela data.
 */
const CalendarMonth = ({
  currentDate,
  appointments,
  professionalColors,
  blockedDates,
  onDayClick,
}) => {
  // Matriz de dias (semanas completas que cobrem o mês corrente).
  const weeks = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: WEEK_STARTS_ON });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: WEEK_STARTS_ON });

    const days = [];
    let cursor = gridStart;
    let guard = 0; // Guard contra loop infinito (máx. 6 semanas = 42 dias).
    while (cursor <= gridEnd && guard < 42) {
      days.push(cursor);
      cursor = addDays(cursor, 1);
      guard += 1;
    }
    const out = [];
    for (let i = 0; i < days.length; i += 7) out.push(days.slice(i, i + 7));
    return out;
  }, [currentDate]);

  // Agrupa agendamentos por dia (BRT). Memoizado — débito do briefing.
  const byDay = useMemo(() => {
    const map = new Map();
    for (const app of appointments ?? []) {
      const key = dayKeyBRT(app.startTime);
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(app);
    }
    return map;
  }, [appointments]);

  const currentMonth = startOfMonth(currentDate).getMonth();
  const todayKey = useMemo(() => dayKeyBRT(new Date()), []);

  return (
    <div className={styles.month}>
      <div className={styles.monthWeekdays}>
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className={styles.monthWeekday}>
            {label}
          </div>
        ))}
      </div>

      <div className={styles.monthGrid}>
        {weeks.map((week, wi) => (
          <div key={wi} className={styles.monthWeek}>
            {week.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const dayApps = byDay.get(key) ?? [];
              const isOutside = day.getMonth() !== currentMonth;
              const isToday = key === todayKey;
              const isBlocked = blockedDates.includes(key);
              const visibleDots = dayApps.slice(0, MAX_DOTS);
              const overflow = dayApps.length - visibleDots.length;

              return (
                <button
                  type="button"
                  key={key}
                  className={[
                    styles.monthCell,
                    isOutside ? styles.monthCellOutside : "",
                    isToday ? styles.monthCellToday : "",
                    isBlocked ? styles.monthCellBlocked : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => onDayClick(day)}
                  aria-label={format(day, "EEEE, dd 'de' MMMM", { locale: ptBR })}
                >
                  <span className={styles.monthDayNumber}>
                    {format(day, "d")}
                  </span>
                  {dayApps.length > 0 && (
                    <span className={styles.monthDots}>
                      {visibleDots.map((app) => (
                        <span
                          key={app.id}
                          className={styles.monthDot}
                          style={{
                            background:
                              professionalColors[app.professionalId] ??
                              getProfessionalColor(app.professionalId),
                          }}
                        />
                      ))}
                      {overflow > 0 && (
                        <span className={styles.monthMore}>+{overflow}</span>
                      )}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CalendarMonth;
